import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from 'react';
import { getShopDomain, isEmbeddedAdmin, isProdBuild } from '../lib/shopContext';

const API_BASE = import.meta.env.VITE_API_URL || '/api/v1';

interface ApiContextValue {
  get: <T = unknown>(path: string) => Promise<T>;
  post: <T = unknown>(path: string, body?: unknown) => Promise<T>;
  patch: <T = unknown>(path: string, body?: unknown) => Promise<T>;
  del: <T = unknown>(path: string) => Promise<T>;
}

const ApiContext = createContext<ApiContextValue | null>(null);

declare global {
  interface Window {
    shopify?: {
      idToken?: () => Promise<string>;
    };
  }
}

/** Wait briefly for App Bridge `window.shopify.idToken` after CDN load. */
async function waitForIdToken(timeoutMs: number): Promise<string | null> {
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    try {
      if (window.shopify?.idToken) {
        const t = await window.shopify.idToken();
        if (t) return t;
      }
    } catch {
      /* App Bridge not ready yet */
    }
    await new Promise((r) => setTimeout(r, 100));
  }
  return null;
}

/**
 * App Store 1.1.1 — production embedded admin:
 *   ONLY App Bridge session tokens (window.shopify.idToken).
 * Dev / non-embedded: optional short-lived app JWT in sessionStorage.
 * localStorage is never used for auth tokens in production.
 *
 * Shopify Embedded checks look for Authorization: Bearer <session_token>
 * on authenticated API calls from the embedded admin.
 */
async function resolveBearerToken(forceFresh = false): Promise<string | null> {
  // App Bridge present (embedded admin) → ONLY session tokens.
  // Never fall back to app JWT — Partner Embedded checks require
  // Authorization: Bearer <idToken()> on authenticated admin API calls.
  const hasAppBridge =
    typeof window !== 'undefined' &&
    !!(window.shopify?.idToken || isEmbeddedAdmin());

  try {
    if (window.shopify?.idToken) {
      // Small delay on force-fresh gives App Bridge a tick to mint a new token
      // after a 401 (expired ~60s session token).
      if (forceFresh) {
        await new Promise((r) => setTimeout(r, 50));
      }
      const t = await window.shopify.idToken();
      if (t) return t;
    }
    if (!forceFresh) {
      // First request may race App Bridge init — wait longer in embed
      const waited = await waitForIdToken(hasAppBridge ? 5000 : 2500);
      if (waited) return waited;
    }
  } catch {
    /* App Bridge not ready */
  }

  if (hasAppBridge || (isProdBuild() && isEmbeddedAdmin())) {
    return null;
  }

  try {
    const sessionJwt = sessionStorage.getItem('gf_token');
    if (sessionJwt) return sessionJwt;
  } catch {
    /* ignore */
  }

  // Dev only — never in production builds
  if (!isProdBuild()) {
    try {
      return localStorage.getItem('gf_token');
    } catch {
      return null;
    }
  }
  return null;
}

export function ApiProvider({ children }: { children: ReactNode }) {
  const request = useCallback(async <T,>(
    path: string,
    init?: RequestInit,
    retried = false,
  ): Promise<T> => {
    const shop = getShopDomain();
    // On retry after 401, force a fresh App Bridge session token
    const token = await resolveBearerToken(retried);

    const res = await fetch(`${API_BASE}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(shop ? { 'X-Shop-Domain': shop } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(init?.headers || {}),
      },
    });

    // Session token expired / not yet valid — retry once with a fresh idToken
    if (res.status === 401 && !retried) {
      try {
        sessionStorage.removeItem('gf_token');
        if (!isProdBuild()) localStorage.removeItem('gf_token');
      } catch {
        /* ignore */
      }
      // Second attempt: resolveBearerToken(true) inside recursive call
      return request(path, init, true);
    }

    if (!res.ok) {
      const text = await res.text();
      let message = text || res.statusText;
      // Surface a clean message for session failures (avoid raw JSON in toasts)
      try {
        const j = JSON.parse(text) as { message?: string; error?: string };
        if (j.message || j.error) {
          message = j.message || j.error || message;
        }
      } catch {
        /* keep text */
      }
      throw new Error(message);
    }
    if (res.status === 204) return undefined as T;
    return res.json() as T;
  }, []);

  const value = useMemo<ApiContextValue>(
    () => ({
      get: (path) => request(path),
      post: (path, body) =>
        request(path, { method: 'POST', body: JSON.stringify(body ?? {}) }),
      patch: (path, body) =>
        request(path, { method: 'PATCH', body: JSON.stringify(body ?? {}) }),
      del: (path) => request(path, { method: 'DELETE' }),
    }),
    [request],
  );

  return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>;
}

export function useApi() {
  const ctx = useContext(ApiContext);
  if (!ctx) throw new Error('useApi must be used within ApiProvider');
  return ctx;
}
